import React from 'react';
import { View } from 'react-native';
import { useAppStore } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useCatLabel, useItemsLabel, useT } from '../i18n';
import { useCategories, useDiscountPresets, useMenuItems, useSettings } from '../menu';
import { usePrompt } from '../prompt';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { EmptyNote } from '../components/EmptyNote';
import { ItemCard } from '../components/ItemCard';
import { ItemGrid } from '../components/ItemGrid';
import { MiniButton } from '../components/MiniButton';
import { SectionTitle } from '../components/SectionTitle';
import { T } from '../components/Text';

export function OffersScreen({ contentPadding }: { contentPadding: number }) {
  const colors = useColors();
  const t = useT();
  const catLabel = useCatLabel();
  const items = useItemsLabel();
  const offers = useAppStore((s) => s.offers);
  const setCategoryOffer = useAppStore((s) => s.setCategoryOffer);
  const clearAllOffers = useAppStore((s) => s.clearAllOffers);
  const openPrompt = usePrompt();
  const categories = useCategories();
  const menuItems = useMenuItems();
  const presets = useDiscountPresets();
  const settings = useSettings();

  const onOffer = menuItems.filter((i) => (offers[i.id] ?? 0) > 0);

  return (
    <Screen contentPadding={contentPadding}>
      <SectionTitle
        title={t('category_offer_title')}
        icon="offers"
        hint={t('category_offer_hint')}
        first
      />

      {categories.map((c) => {
        const itemsInCat = menuItems.filter((i) => i.cat === c.id);
        const activeCount = itemsInCat.filter((i) => (offers[i.id] ?? 0) > 0).length;
        return (
          <View
            key={c.id}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              paddingVertical: 12,
              paddingHorizontal: 14,
              backgroundColor: colors.surface2,
              borderRadius: radius.md,
              borderWidth: 1,
              borderColor: activeCount ? colors.goldDim : colors.line,
              marginBottom: 10,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, flexGrow: 1, flexBasis: 180 }}>
              <Icon name={c.icon as IconName} size={18} color={colors.gold} />
              <View style={{ gap: 3, flexShrink: 1 }}>
                <T size={14} weight={700}>
                  {catLabel(c.id)}
                </T>
                <T size={11.5} color={colors.textFaint}>
                  {items(itemsInCat.length)}
                  {activeCount ? ` · ${activeCount} ${t('off')}` : ''}
                </T>
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              {presets.map((pct) => (
                <MiniButton
                  key={pct}
                  label={`${pct}%`}
                  flex={false}
                  onPress={() => setCategoryOffer(c.id, pct)}
                />
              ))}
              <MiniButton
                label={t('custom')}
                flex={false}
                onPress={() =>
                  openPrompt({
                    title: t('custom_discount_for', { name: catLabel(c.id) }),
                    hint: t('discount_range_hint', { max: settings.maxDiscountPct }),
                    onSubmit: (v) => setCategoryOffer(c.id, Number(v)),
                  })
                }
              />
              <MiniButton label={t('clear')} flex={false} onPress={() => setCategoryOffer(c.id, 0)} />
            </View>
          </View>
        );
      })}

      <SectionTitle
        title={`${t('items_on_offer')} (${onOffer.length})`}
        icon="percent"
        hint={onOffer.length ? t('clear_all_offers') : undefined}
        hintIcon={onOffer.length ? 'trash' : undefined}
        hintColor={colors.spice}
        onHintPress={onOffer.length ? clearAllOffers : undefined}
      />

      {onOffer.length ? (
        <ItemGrid>
          {onOffer.map((item) => (
            <ItemCard key={item.id} item={item} mode="manage" />
          ))}
        </ItemGrid>
      ) : (
        <EmptyNote>{t('no_offers')}</EmptyNote>
      )}
    </Screen>
  );
}
